/**
 * Ronde 4 W5 — Trends & Analyse module (spec ronde-4/05-trends-analyse-module.md).
 *
 * Gedeelde DTO's + enum-constants + status-labels voor de Trends-module.
 * Database modellen leven in `packages/db/prisma/schema.prisma`; deze types
 * spiegelen de relevant subset voor API + UI.
 */

// ─── Enums ───────────────────────────────────────────────────────────────────

export const TREND_CATEGORIEEN = [
  'INTERN',
  'EXTERN',
  'LEADS',
  'FINANCIEEL',
  'APP_GEBRUIK',
  'PLANNING',
  'WERKBONNEN',
  'VERLONING',
  'ZIEKTE_VERLOF',
  'INTERCOMPANY',
  // Batch-6 hrm — personeels-, relatie- en acquisitie-trends.
  'HR',
  'CRM',
  'MARKETING',
] as const

export type TrendCategorie = (typeof TREND_CATEGORIEEN)[number]

export const TREND_CALCULATION_TYPES = [
  'COUNT',
  'SUM',
  'AVERAGE',
  'PERCENTAGE',
  'DURATION',
  'RATE',
  'DISTRIBUTION',
  'COMPOSITE',
] as const

export type TrendCalculationType = (typeof TREND_CALCULATION_TYPES)[number]

export const TREND_FREQUENTIES = [
  'REALTIME',
  'DAGELIJKS',
  'WEKELIJKS',
  'MAANDELIJKS',
  'KWARTAAL',
] as const

export type TrendFrequentie = (typeof TREND_FREQUENTIES)[number]

export const TREND_RUN_STATUSES = ['BEZIG', 'GEREED', 'MISLUKT'] as const
export type TrendRunStatus = (typeof TREND_RUN_STATUSES)[number]

export const AI_ANALYSIS_STATUSES = ['BEZIG', 'GEREED', 'MISLUKT'] as const
export type AIAnalysisStatus = (typeof AI_ANALYSIS_STATUSES)[number]

export const TREND_ALERT_LEVELS = ['rood', 'oranje', 'groen'] as const
export type TrendAlertLevel = (typeof TREND_ALERT_LEVELS)[number]

// ─── Labels (NL) ─────────────────────────────────────────────────────────────

export const TREND_CATEGORIE_LABELS: Record<TrendCategorie, string> = {
  INTERN:        'Intern',
  EXTERN:        'Extern',
  LEADS:         'Leads',
  FINANCIEEL:    'Financieel',
  APP_GEBRUIK:   'App-gebruik',
  PLANNING:      'Planning',
  WERKBONNEN:    'Werkbonnen',
  VERLONING:     'Verloning',
  ZIEKTE_VERLOF: 'Ziekte/Verlof',
  INTERCOMPANY:  'Intercompany',
  HR:            'HR',
  CRM:           'CRM',
  MARKETING:     'Marketing',
}

/** Tab-volgorde in /trends (categorie-tabs; HR/CRM/MARKETING zijn batch-6). */
export const TREND_TAB_ORDER: readonly TrendCategorie[] = [
  'INTERN',
  'HR',
  'EXTERN',
  'CRM',
  'LEADS',
  'MARKETING',
  'FINANCIEEL',
  'APP_GEBRUIK',
  'PLANNING',
  'WERKBONNEN',
  'VERLONING',
  'ZIEKTE_VERLOF',
  'INTERCOMPANY',
]

export const TREND_FREQUENTIE_LABELS: Record<TrendFrequentie, string> = {
  REALTIME:     'Realtime',
  DAGELIJKS:    'Dagelijks',
  WEKELIJKS:    'Wekelijks',
  MAANDELIJKS:  'Maandelijks',
  KWARTAAL:     'Per kwartaal',
}

export const TREND_RUN_STATUS_LABELS: Record<TrendRunStatus, string> = {
  BEZIG:   'Bezig',
  GEREED:  'Gereed',
  MISLUKT: 'Mislukt',
}

export const AI_ANALYSIS_STATUS_LABELS: Record<AIAnalysisStatus, string> = {
  BEZIG:   'Bezig',
  GEREED:  'Gereed',
  MISLUKT: 'Mislukt',
}

export const TREND_CALCULATION_TYPE_LABELS: Record<TrendCalculationType, string> = {
  COUNT:        'Telling',
  SUM:          'Som',
  AVERAGE:      'Gemiddelde',
  PERCENTAGE:   'Percentage',
  DURATION:     'Tijdsduur',
  RATE:         'Ratio',
  DISTRIBUTION: 'Verdeling',
  COMPOSITE:    'Samengesteld',
}

// ─── DTO's ───────────────────────────────────────────────────────────────────

// Batch-6 hrm — HR-trend-rapportage (rapportage-hr "Trends"-sectie). De
// details-shape spiegelt TrendQueryDetail van de engine; de calculator heeft
// k-anonimiteit al toegepast (cellen < 5 medewerkers samengevoegd).
export interface HrTrendDetailDTO {
  groupByType: string
  groupById: string
  groupByLabel: string
  value: number
}

export interface HrTrendResultDTO {
  value: number
  details: HrTrendDetailDTO[]
}

/** Maandelijkse snapshot (peildatum = maandeinde) voor tijdsverloop-grafieken. */
export interface HrTrendSnapshotDTO {
  /** yyyy-MM-dd (peildatum, NL-kalenderdag). */
  peildatum: string
  value: number
  details: HrTrendDetailDTO[]
}

/** Kerncijfers uren/reisuren/km voor de rapportage-hr kaarten. */
export interface HrKerncijfersDTO {
  uren: HrTrendResultDTO
  reisuren: HrTrendResultDTO
  kilometers: HrTrendResultDTO
}

/**
 * Filters die op alle trends werken (spec §8.2). Subset wordt per
 * trend afgedwongen via TrendDefinition.availableFilters.
 */
export interface TrendFilter {
  /** BV / entiteit-ID. */
  entityId?: string
  /** Team/afdeling-ID. */
  teamId?: string
  /** Specifieke medewerker-ID. */
  employeeId?: string
  /** Specifieke klant-ID. */
  clientId?: string
  /** Werksoort-filter (ONDERHOUD, SERVICE, PROJECT, ...). */
  werkbonType?: string
  /** Dienst/rooster-type (DAGDIENST, PLOEGEN_2, ...). */
  dienstType?: string
  /** Contract-type (VAST, FLEX, UITZEND, ...). */
  contractType?: string
  /** Locatie-ID indien relevant voor de trend. */
  locationId?: string
  /** Status-filter (bv. werkbon-status). */
  status?: string
}

export interface TrendDefinitionDto {
  id: string
  code: string
  naam: string
  beschrijving: string | null
  categorie: TrendCategorie
  calculationType: TrendCalculationType
  queryKey: string
  eenheid: string
  enabled: boolean
  frequentie: TrendFrequentie
  retentionMonths: number
  availableFilters: string[]
  dataSources: string[]
  defaultPeriodDays: number
  drempels: TrendDrempels | null
  createdAt: string
  updatedAt: string
}

/** Drempels-shape gebruikt voor alert-niveau bepaling (spec §1.1). */
export interface TrendDrempels {
  rood?: number
  oranje?: number
  /** Optioneel: richting van de drempel — 'hoger' (overschrijden = rood)
   * of 'lager' (onderschrijden = rood). Default 'hoger'. */
  richting?: 'hoger' | 'lager'
}

export interface TrendMetricDto {
  id: string
  metricKey: string
  value: number
  unit: string | null
  previousValue: number | null
  changePct: number | null
  groupByType: string | null
  groupById: string | null
  groupByLabel: string | null
}

export interface TrendRunDto {
  id: string
  trendDefinitionId: string
  startedBy: string | null
  startedAt: string
  completedAt: string | null
  periodStart: string
  periodEnd: string
  vergelijkPeriodStart: string | null
  vergelijkPeriodEnd: string | null
  filters: TrendFilter | null
  status: TrendRunStatus
  calculationVersion: string
  superseded: boolean
  errorMessage: string | null
}

export interface TrendDashboardCard {
  definitionId: string
  code: string
  naam: string
  categorie: TrendCategorie
  eenheid: string
  laatsteWaarde: number | null
  vorigeWaarde: number | null
  veranderingPct: number | null
  laatstBerekend: string | null
  laatsteRunId: string | null
  heeftAiAnalyse: boolean
  alertLevel: TrendAlertLevel | null
  // Laatste-poging-status (fix-trends deel 2): zichtbaar maken dat een trend een
  // mislukte/overgeslagen poging had i.p.v. een eeuwig "nog geen berekening".
  frequentie: TrendFrequentie
  /** Status van de laatste run-poging (élke status), null = nog nooit gedraaid. */
  laatsteRunStatus: TrendRunStatus | null
  /** Foutreden bij een MISLUKT-poging (bv. onbekende queryKey). */
  laatsteRunFout: string | null
  /** Tijdstip van de laatste poging (ISO), ongeacht status. */
  laatstePogingOp: string | null
  /** Eerstvolgende cron-moment (ISO, NL-tijd-afgeleid), null bij REALTIME. */
  volgendeCronMoment: string | null
}

export interface AITrendAnalysisDto {
  id: string
  trendRunId: string
  startedBy: string
  startedAt: string
  completedAt: string | null
  model: string
  prompt: string
  filters: TrendFilter | null
  dataSummary: unknown
  result: unknown
  recommendations: string[] | null
  confidence: number | null
  status: AIAnalysisStatus
  errorMessage: string | null
  inputTokens: number | null
  outputTokens: number | null
  duurMs: number | null
  visibleToRoles: string[]
}

/** Output-shape van de Claude-analyse (subset van AnalyseResult). */
export interface TrendAnalyseResult {
  samenvatting: string
  patronen: Array<{
    titel: string
    beschrijving: string
    ernst: 'hoog' | 'normaal' | 'laag'
  }>
  afwijkingen: Array<{
    metric: string
    verwacht: string
    werkelijk: string
    mogelijkeOorzaak: string
  }>
  aanbevelingen: string[]
  risicos: string[]
  vervolgvragen: string[]
  confidence: number
}

/** Per-trend permissie-DTO (spec §1.5). */
export interface TrendPermissionDto {
  id: string
  trendDefinitionId: string
  role: string
  canView: boolean
  canExport: boolean
  canStartAiAnalysis: boolean
  canRecalculate: boolean
  canToggleTracking: boolean
}
